/**
 * The Daily log's detail pane (LED-1152, design "Daily log · detail").
 *
 * An EDIT surface that mirrors the review card (`ReviewQueue`'s record card):
 * the record type's pill and its stamp, an editable title, the record type's
 * own fields as the review card's pill tray (`FieldRows layout="pills"` —
 * which brings the name-matching pill with it), the photos, then meta rows,
 * the activity and the save state. There is NO Accept or Reject here: these
 * are entries already on the log. An entry still waiting in review says so
 * and links to its review card instead of being edited here.
 *
 * ## Saving
 *
 * Every edit saves through the console's post-review edit path
 * (`POST /api/items/:kind/:id/edit` → `JobSiteManager.updateReviewedItem`),
 * flushed on blur and after a short pause, one request at a time. A
 * Procore-bound entry then reads "Saved · syncing to Procore" until the
 * record's own sync state says it landed ("Synced").
 *
 * ## A manpower conflict
 *
 * A manpower entry whose company already has a row on that day's Procore log
 * (and no chosen outcome yet) shows the conflict panel IN PLACE of the pill
 * tray, with the panel's Save wording. Saving sends the choice as an "edited"
 * review decision — the one write that records a manpower merge choice — and
 * the pane returns to the ordinary pills.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Camera, Check, Link2, List, MapPin, Plus, UserRound, X } from "lucide-react";
import {
  deleteMutation, fetchItemDetail, postItemDelete, postItemEdit, postMutation, postReviewDecision,
  type ConsoleRow, type DailyLogWalkRow, type ItemDetailResponse, type ItemEvidenceRow, type ItemHistoryRow,
} from "../api";
import { useResource } from "../useResource";
import { C, DL, FONT_MONO, FONT_TEXT } from "../tokens";
import { ASK_COMPOSER_CLEARANCE, clockOf, whenStamp } from "../components/chrome";
import { PhotoImage } from "../components/PhotoImage";
import { PhotoFullscreenViewer } from "../components/PhotoFullscreenViewer";
import { RecordPhotoPicker } from "../components/RecordPhotoPicker";
import {
  ManpowerConflictPanel, manpowerConflictActionLabel, manpowerConflictChoice, manpowerConflictSubmission,
  type ManpowerMergeResolution,
} from "../components/ManpowerConflictPanel";
import { readRecordPhoto } from "../lib/recordPhoto";
import { copyToClipboard } from "../lib/clipboard";
import { mobileRecordFields } from "../mobileRecordPresentation";
import { FieldRows } from "./ReviewQueue";
import { historySentence } from "./ItemDetail";
import { procoreSyncLook } from "./RecordDetail";
import { hasOpenManpowerConflict, singularLabel } from "./dailyLogModel";
import { readManpowerCount } from "../../../../backend/src/shared/manpowerMerge";
import {
  PROCORE_DAILY_LOG_CATEGORY_ORDER,
  getProcoreDailyLogCategoryLabel,
} from "../../../../backend/src/shared/procoreDailyLog";

type SaveState = { kind: "idle" | "dirty" | "saving" | "saved" } | { kind: "failed"; message: string };

/** A record has one writer. New edits wait for the current request, and the
 * latest complete field patch wins before the next request starts. */
function useEntrySave(row: ConsoleRow, search: string, onSaved: () => void) {
  const [state, setState] = useState<SaveState>({ kind: "idle" });
  const pending = useRef<Record<string, unknown> | null>(null);
  const running = useRef<Promise<boolean> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  const saved = useRef(onSaved);
  saved.current = onSaved;

  const flush = (): Promise<boolean> => {
    if (timer.current) clearTimeout(timer.current);
    if (running.current) return running.current;
    if (!pending.current) return Promise.resolve(true);
    const work = async () => {
      while (pending.current) {
        const patch = pending.current;
        pending.current = null;
        if (mounted.current) setState({ kind: "saving" });
        const result = await postItemEdit(row.kind, row.id, search, patch);
        if (!result.ok) {
          pending.current = { ...patch, ...(pending.current ?? {}) };
          if (mounted.current) setState({ kind: "failed", message: result.error ?? "Could not save this entry." });
          return false;
        }
        if (mounted.current) saved.current();
      }
      if (mounted.current) setState({ kind: "saved" });
      return true;
    };
    running.current = work().finally(() => { running.current = null; });
    return running.current;
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // A navigation cannot cancel an edit the user has already made.
      void flushRef.current();
    };
  }, []);

  const change = (patch: Record<string, unknown>) => {
    pending.current = { ...(pending.current ?? {}), ...patch };
    if (!running.current) setState({ kind: "dirty" });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flushRef.current(), 700);
  };
  return { state, change, flush, setState };
}

/**
 * The pane's height: sticky 12px under the top of the page's scroller, and
 * never taller than what is left above the Ask composer — so its own body
 * scrolls instead of running under the composer.
 */
export function usePaneHeight(ref: { current: HTMLElement | null }, key: unknown): number | undefined {
  const [height, setHeight] = useState<number>();
  useLayoutEffect(() => {
    const panel = ref.current;
    const scroller = panel?.parentElement?.closest<HTMLElement>(".sb");
    if (!panel || !scroller) return;
    const fit = () => {
      const frame = scroller.getBoundingClientRect();
      const rect = panel.getBoundingClientRect();
      // The clearance less its reading room (~60px): the pane may run down to
      // the composer's own top edge, where a page's last row may not.
      const bottom = Math.min(window.innerHeight, frame.bottom) - (ASK_COMPOSER_CLEARANCE - 60);
      setHeight(Math.max(160, bottom - Math.max(rect.top, frame.top + 12)));
    };
    fit();
    window.addEventListener("resize", fit);
    scroller.addEventListener("scroll", fit, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(fit);
    observer?.observe(scroller);
    return () => { window.removeEventListener("resize", fit); scroller.removeEventListener("scroll", fit); observer?.disconnect(); };
  }, [ref, key]);
  return height;
}

/** `"Manpower"`, `"Delivery"` — the record type, one entry's worth. */
export function entryKindLabel(category: string | undefined): string {
  if (!category) return "Entry";
  const known = (PROCORE_DAILY_LOG_CATEGORY_ORDER as readonly string[]).includes(category);
  return singularLabel(known ? getProcoreDailyLogCategoryLabel(category as never) : category.replace(/[_-]+/g, " "));
}

/** One line of the pane's activity, newest first. */
export interface PaneActivity {
  id: string;
  mark: string;
  tone: "ok" | "person" | "led" | "bad";
  who: string;
  what: string;
  when: string;
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]!.toUpperCase()).join("") || "?";
}

function shortDay(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * The activity, in the design's words: "Procore synced to the Oct 2 daily
 * log", "{creator} accepted it in review", "Led AI drafted it from {walk}".
 * From the record's logged events; when the log has none of a kind, the
 * record's own state supplies it (an accepted record WAS accepted, a synced
 * one WAS synced), so an entry from before events were logged still reads.
 */
export function paneActivity(args: {
  history: ItemHistoryRow[];
  item: ConsoleRow;
  creator?: string;
  walkName?: string;
  logDay: string;
}): PaneActivity[] {
  const { history, item, creator, walkName, logDay } = args;
  const lines: Array<PaneActivity & { at: number }> = [];
  const named = (entry: ItemHistoryRow) => (entry.actor && !("system" in entry.actor) ? entry.actor.displayName : undefined);
  for (const entry of history) {
    const at = Date.parse(entry.at);
    const when = whenStamp(entry.at).toUpperCase();
    if (entry.type === "synced") {
      lines.push({ id: entry.id, at, mark: "✓", tone: "ok", who: "Procore", what: `synced to the ${logDay} daily log`, when });
    } else if (entry.type === "sync_failed") {
      lines.push({ id: entry.id, at, mark: "!", tone: "bad", who: "Procore", what: "sync failed", when });
    } else if (entry.type === "reviewed") {
      const who = named(entry) ?? creator ?? "Someone";
      lines.push({ id: entry.id, at, mark: initials(who), tone: "person", who, what: "accepted it in review", when });
    } else if (entry.type === "drafted") {
      lines.push({ id: entry.id, at, mark: "L", tone: "led", who: "Led AI", what: walkName ? `drafted it from ${walkName}` : "drafted it from the walk", when });
    } else {
      const who = named(entry);
      const sentence = historySentence(entry, item);
      const what = who && sentence.startsWith(`${who} `) ? sentence.slice(who.length + 1) : sentence;
      lines.push({ id: entry.id, at, mark: who ? initials(who) : "·", tone: "person", who: who ?? "", what, when });
    }
  }
  const has = (type: ItemHistoryRow["type"]) => history.some((entry) => entry.type === type);
  const sync = item.procoreSync;
  if (!has("synced") && sync?.status === "synced") {
    const iso = sync.syncedAt ?? sync.attemptedAt;
    lines.push({ id: "state-synced", at: iso ? Date.parse(String(iso)) : Date.parse(item.lastMovementAt), mark: "✓", tone: "ok", who: "Procore", what: `synced to the ${logDay} daily log`, when: iso ? whenStamp(String(iso)).toUpperCase() : "" });
  }
  if (!has("reviewed") && item.reviewStatus === "accepted") {
    const who = creator ?? "Someone";
    lines.push({ id: "state-reviewed", at: Date.parse(item.createdAt) + 1, mark: initials(who), tone: "person", who, what: "accepted it in review", when: "" });
  }
  if (!has("drafted")) {
    lines.push({ id: "state-drafted", at: Date.parse(item.createdAt), mark: "L", tone: "led", who: "Led AI", what: walkName ? `drafted it from ${walkName}` : "drafted it from the walk", when: whenStamp(item.createdAt).toUpperCase() });
  }
  return lines.sort((a, b) => b.at - a.at).map(({ at: _at, ...line }) => line);
}

const MARK_TONE: Record<PaneActivity["tone"], { bg: string; fg: string }> = {
  ok: { bg: DL.okBg, fg: DL.okInk },
  person: { bg: DL.ink, fg: DL.card },
  led: { bg: DL.led, fg: DL.ink },
  bad: { bg: DL.deleteHover, fg: DL.failInk },
};

const MONO: CSSProperties = { fontFamily: FONT_MONO, fontWeight: 700, textTransform: "uppercase" };

/** The record type's pill — the review card's chip, read-only here: moving an
 *  accepted entry to another section is not an edit this pane makes. */
function KindPill({ label }: { label: string }) {
  return (
    <span data-testid="daily-log-editor-kind" style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 32, padding: "0 12px", borderRadius: 999, background: DL.kindBg, color: DL.kindInk }}>
      <List size={13} strokeWidth={2.2} aria-hidden />
      <span style={{ ...MONO, fontSize: 10.5, letterSpacing: "1.5px", whiteSpace: "nowrap" }}>{label}</span>
    </span>
  );
}

/** An auto-growing title that saves on blur. */
function TitleEditor({ value, disabled, onChange, onBlur }: { value: string; disabled?: boolean; onChange: (next: string) => void; onBlur: () => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${node.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      data-testid="daily-log-editor-title"
      aria-label="Entry title"
      rows={1}
      value={value}
      readOnly={disabled}
      className="review-editable"
      // Both, as the review card's AutoTextarea does: `onInput` is the raw
      // event, `onChange` React's tracked one.
      onInput={(event) => onChange(event.currentTarget.value)}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      style={{
        display: "block", width: "calc(100% + 12px)", boxSizing: "border-box", margin: "14px -6px 0", padding: "2px 6px",
        border: "none", outline: "none", borderRadius: 6, resize: "none", overflow: "hidden",
        fontFamily: FONT_TEXT, fontSize: 22, fontWeight: 800, letterSpacing: "-0.7px", lineHeight: 1.15, color: DL.ink,
      }}
    />
  );
}

/** The design's media block: label + Add, then a 2-up grid with remove. */
function EntryMedia({
  photos, row, busy, error, canEdit, onAdd, onRemove,
}: {
  photos: ItemEvidenceRow[];
  row: ConsoleRow;
  busy: boolean;
  error: string | null;
  canEdit: boolean;
  onAdd: () => void;
  onRemove: (id: string) => void;
}) {
  const [viewer, setViewer] = useState<number | null>(null);
  return (
    <section data-testid="daily-log-editor-photos" aria-label="Entry photos" style={{ marginTop: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ ...MONO, display: "inline-flex", alignItems: "center", gap: 7, fontSize: 10, letterSpacing: "1.4px", color: DL.muted }}>
          <Camera size={12} strokeWidth={2} aria-hidden />
          {`Media · ${photos.length} ${photos.length === 1 ? "photo" : "photos"}`}
        </span>
        {canEdit && (
          <button type="button" className="kb" data-testid="daily-log-editor-add-photo" disabled={busy} onClick={onAdd} style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 30, padding: "0 12px", borderRadius: 15, border: `1px solid ${DL.control}`, background: DL.card, fontSize: 13, fontWeight: 600, color: DL.ink, cursor: busy ? "default" : "pointer" }}>
            <Plus size={11} strokeWidth={2.4} aria-hidden />Add
          </button>
        )}
      </div>
      {photos.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, marginTop: 10 }}>
          {photos.map((photo, index) => (
            <div key={photo.id} style={{ position: "relative", aspectRatio: "1", borderRadius: 12, overflow: "hidden", background: DL.photoGround }}>
              <button
                type="button" className="kb" data-testid="daily-log-editor-photo"
                aria-label={`Open photo ${index + 1} of ${photos.length}`}
                onClick={() => setViewer(index)}
                style={{ display: "block", width: "100%", height: "100%", padding: 0, border: 0, cursor: "zoom-in" }}
              >
                {(photo.thumbnailUrl ?? photo.url) && <PhotoImage src={(photo.thumbnailUrl ?? photo.url)!} alt={photo.description ?? `Photo ${index + 1}`} style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />}
              </button>
              {canEdit && (
                <button type="button" className="kb" title="Remove" aria-label="Remove photo" disabled={busy} onClick={() => onRemove(photo.id)} style={{ position: "absolute", right: 8, top: 8, width: 26, height: 26, borderRadius: 13, border: 0, background: DL.card, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <X size={11} color={DL.deleteLine} strokeWidth={2.6} aria-hidden />
                </button>
              )}
              <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "22px 10px 8px", background: `linear-gradient(180deg, transparent, ${DL.overlay})`, pointerEvents: "none" }}>
                <div style={{ fontFamily: FONT_MONO, fontSize: 9, color: DL.card }}>{clockOf(new Date(photo.capturedAt))}</div>
                {photo.description && <div style={{ fontSize: 12, fontWeight: 600, color: DL.card, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 }}>{photo.description}</div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ marginTop: 10, padding: 18, borderRadius: 12, border: `1.5px dashed ${DL.control}`, textAlign: "center", fontSize: 13, color: DL.muted }}>No photos on this entry</div>
      )}
      {error && <div role="alert" style={{ marginTop: 8, fontSize: 12.5, color: DL.failInk }}>{error}</div>}
      {viewer !== null && photos[viewer] && <PhotoFullscreenViewer
        photos={photos.map((entry) => ({ ...entry, walkName: row.title }))}
        index={viewer} onStep={setViewer} onClose={() => setViewer(null)}
      />}
    </section>
  );
}

function MetaRow({ icon, label, value, testId }: { icon: ReactNode; label: string; value: string; testId?: string }) {
  return (
    <div data-testid={testId} style={{ display: "flex", alignItems: "center", gap: 10, height: 38, borderBottom: `1px solid ${DL.rowLine}`, fontSize: 13 }}>
      <span style={{ display: "inline-flex", color: DL.muted }}>{icon}</span>
      <span style={{ color: DL.muted }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "right" }}>{value}</span>
    </div>
  );
}

/** Saved → syncing → Synced, read off the record's own Procore state. */
type SyncPhase = "none" | "syncing" | "synced" | "failed";

export function DailyLogEntryEditor({
  row, walk, search, refreshKey, reviewHref, detailHref, onOpenDetail, onNavigate, onClose, onSaved, onDeleted, beforeLeave, viewerName, photoFallback,
}: {
  row: ConsoleRow;
  walk: DailyLogWalkRow | null;
  search: string;
  refreshKey: number;
  reviewHref: string;
  detailHref: string;
  onOpenDetail: () => void;
  onNavigate: (href: string) => void;
  onClose: () => void;
  onSaved: () => void;
  /** The entry is gone; the host closes the pane and refreshes the day. */
  onDeleted?: () => void;
  beforeLeave: { current: (() => Promise<boolean>) | null };
  /** Who "you" are, for the edit line in the activity. */
  viewerName?: string;
  /** The walk's own copy of a photo, for one the record read could not sign. */
  photoFallback?: (mediaId: string) => string | null;
}) {
  const [revision, setRevision] = useState(0);
  const [title, setTitle] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelHeight = usePaneHeight(panelRef, row.id);

  const { data, failure } = useResource<ItemDetailResponse>(
    `daily-log-entry:${row.id}:${search}`,
    () => fetchItemDetail(row.id, search),
    refreshKey + revision,
  );
  const loaded = data?.item?.id === row.id;
  const item = loaded ? data!.item : row;
  const editorItem = { ...item, recordType: item.recordType ?? "daily_log" as const };
  const pending = item.reviewStatus === "pending";
  const procore = item.syncSource === "procore";
  const fields = mobileRecordFields(editorItem) ?? [];

  // ---- saving, and the sync after it -------------------------------------
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [syncPhase, setSyncPhase] = useState<SyncPhase>("none");
  const syncedBefore = useRef<string | undefined>(undefined);
  const sawPending = useRef(false);
  const afterSave = () => {
    syncedBefore.current = item.procoreSync?.syncedAt ? String(item.procoreSync.syncedAt) : undefined;
    sawPending.current = false;
    setSavedAt(Date.now());
    if (procore) setSyncPhase("syncing");
    setRevision((value) => value + 1);
    onSaved();
  };
  const save = useEntrySave(row, search, afterSave);
  useEffect(() => {
    beforeLeave.current = save.flush;
    return () => { beforeLeave.current = null; };
  }, [beforeLeave, save.flush]);
  // While a Procore push is outstanding, read the record again until its
  // sync state settles. Bounded: a push that never reports back leaves the
  // line at "syncing", which is the truth as far as this page knows it.
  useEffect(() => {
    if (syncPhase !== "syncing" || !loaded) return;
    const sync = item.procoreSync;
    if (sync?.status === "pending" || sync?.status === "syncing") sawPending.current = true;
    const landed = sync?.status === "synced" && (sawPending.current || String(sync.syncedAt ?? "") !== (syncedBefore.current ?? ""));
    if (landed) { setSyncPhase("synced"); return; }
    if (sync?.status === "failed" || sync?.status === "blocked" || sync?.status === "conflict") { setSyncPhase("failed"); return; }
    if (savedAt && Date.now() - savedAt > 45_000) return;
    const timer = setTimeout(() => setRevision((value) => value + 1), 1500);
    return () => clearTimeout(timer);
    // `data`, not just the status: a read that comes back still pending must
    // schedule the next one.
  }, [syncPhase, loaded, data, savedAt]);

  const leave = async (action: () => void) => { if (await save.flush()) action(); };

  // ---- media ---------------------------------------------------------------
  const photos = (loaded ? data!.evidence ?? [] : [])
    .filter((entry) => entry.type === "image")
    .map((entry) => entry.url || entry.thumbnailUrl || !photoFallback ? entry : { ...entry, url: photoFallback(entry.id) });
  const [mediaBusy, setMediaBusy] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const mediaPath = `/api/items/${row.kind}/${encodeURIComponent(row.id)}/media`;
  const changeMedia = async (work: () => Promise<{ ok: boolean; error?: string }[]>) => {
    setMediaBusy(true);
    setMediaError(null);
    try {
      const results = await work();
      const failed = results.find((result) => !result.ok);
      if (failed) setMediaError(failed.error ?? "Could not update photos.");
      else afterSave();
    } catch (error) {
      setMediaError(error instanceof Error ? error.message : "Could not update photos.");
    } finally {
      setMediaBusy(false);
      setRevision((value) => value + 1);
    }
  };

  // ---- the conflict ----------------------------------------------------------
  const merge = hasOpenManpowerConflict(item) ? item.manpowerMerge : undefined;
  const target = merge ? merge.candidates.find((candidate) => candidate.remoteId === merge.targetRemoteId) ?? merge.candidates[0] : undefined;
  const [mergeChoice, setMergeChoice] = useState<ManpowerMergeResolution | null>(null);
  const [conflictBusy, setConflictBusy] = useState(false);
  const [conflictError, setConflictError] = useState<string | null>(null);
  const conflict = !pending && merge && target ? {
    variant: merge.variant,
    choice: manpowerConflictChoice(merge.variant, mergeChoice, merge.suggested),
    existing: { company: target.companyLabel ?? String(item.fieldValues?.vendor ?? ""), workers: target.workers, hours: target.hours, location: target.locationLabel },
    walk: {
      company: String(item.fieldValues?.vendor ?? target.companyLabel ?? ""),
      workers: readManpowerCount(item.fieldValues?.workers),
      hours: readManpowerCount(item.fieldValues?.hours),
      location: item.location?.name ?? (typeof item.fieldValues?.location === "string" ? item.fieldValues.location : null),
    },
  } : null;
  const applyConflict = async () => {
    if (!conflict || !target) return;
    if (!(await save.flush())) return;
    setConflictBusy(true);
    setConflictError(null);
    const patch = manpowerConflictSubmission({
      choice: conflict.choice,
      targetRemoteId: target.remoteId,
      existingWorkers: target.workers,
      walkWorkers: conflict.walk.workers,
      fieldValues: { ...(item.fieldValues ?? {}) },
    });
    const result = await postReviewDecision(row.kind, row.id, search, "edited", patch);
    setConflictBusy(false);
    if (!result.ok) { setConflictError(result.error ?? "Could not save this choice."); return; }
    setMergeChoice(null);
    save.setState({ kind: "saved" });
    afterSave();
  };

  // ---- delete ------------------------------------------------------------------
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const remove = async () => {
    if (!deleteArmed) { setDeleteArmed(true); return; }
    setDeleteBusy(true);
    setDeleteError(null);
    const result = await postItemDelete(row.kind, row.id, search);
    setDeleteBusy(false);
    if (!result.ok) { setDeleteError(result.error ?? "Could not delete this entry."); setDeleteArmed(false); return; }
    beforeLeave.current = null;
    (onDeleted ?? onClose)();
  };
  useEffect(() => { setDeleteArmed(false); }, [row.id]);

  // ---- words -------------------------------------------------------------------
  const kindLabel = entryKindLabel(item.category);
  const creator = data?.creator?.name ?? walk?.walkerName;
  const logDay = shortDay(item.createdAt);
  const [copied, setCopied] = useState(false);
  const editedSince = savedAt !== null && (data?.history ?? []).some((entry) => entry.type === "edited" && Date.parse(entry.at) >= savedAt - 10_000);
  const activity: PaneActivity[] = [
    ...(savedAt !== null && !editedSince ? [{
      id: "just-now", mark: "✓", tone: "ok" as const, who: viewerName || "You",
      what: procore ? (syncPhase === "synced" ? "edited this entry · synced to Procore" : "edited this entry · syncing to Procore") : "edited this entry",
      when: "JUST NOW",
    }] : []),
    ...paneActivity({ history: loaded ? data!.history ?? [] : [], item, creator, walkName: walk?.name, logDay }),
  ];
  const look = procoreSyncLook(item);
  const procoreLine = !procore ? "Not linked" : look?.tone === "ok" ? "Synced · edits sync on save" : look?.tone === "bad" ? look.label : look ? look.short : "Edits sync on save";

  const saveLine = (() => {
    if (pending) return { text: "Waiting in review", fg: DL.label };
    switch (save.state.kind) {
      case "failed": return { text: save.state.message, fg: DL.failInk };
      case "dirty":
      case "saving": return { text: "Saving…", fg: DL.label };
      case "saved":
        if (syncPhase === "syncing") return { text: "Saved · syncing to Procore", fg: DL.okInk };
        if (syncPhase === "synced") return { text: "Synced", fg: DL.okInk };
        if (syncPhase === "failed") return { text: "Saved · Procore sync failed", fg: DL.failInk };
        return { text: "Saved", fg: DL.okInk };
      default: return { text: "Edits save automatically", fg: DL.label };
    }
  })();

  return (
    <div ref={panelRef} data-testid="daily-log-editor" data-item-id={row.id} style={{
      width: "100%", boxSizing: "border-box", minWidth: 0,
      background: DL.card, border: `1px solid ${DL.hairline}`, borderRadius: 16,
      position: "sticky", top: 12,
      display: "flex", flexDirection: "column", maxHeight: panelHeight ?? `calc(100dvh - ${ASK_COMPOSER_CLEARANCE + 40}px)`, overflow: "hidden",
      animation: "dlPaneIn .18s cubic-bezier(.2,.8,.2,1) both",
    }}>
      <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 8, height: 46, padding: "0 10px 0 16px", borderBottom: `1px solid ${DL.hairline}` }}>
        <span data-testid="daily-log-editor-crumb" style={{ ...MONO, fontSize: 9.5, letterSpacing: "1.2px", color: DL.muted }}>{`Daily log · ${kindLabel}`}</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="kb dl-icon" title={copied ? "Link copied" : "Copy link"} aria-label="Copy link" onClick={async () => {
          const ok = await copyToClipboard(new URL(detailHref, window.location.href).toString());
          setCopied(ok);
          if (ok) setTimeout(() => setCopied(false), 1400);
        }} style={iconButton}>
          {copied ? <Check size={13} color={DL.okInk} strokeWidth={2.4} aria-hidden /> : <Link2 size={13} color={DL.ink2} strokeWidth={2} aria-hidden />}
        </button>
        <button type="button" className="kb dl-icon" data-testid="daily-log-editor-close" aria-label="Close the editor" onClick={() => void leave(onClose)} style={iconButton}>
          <X size={14} color={DL.ink2} strokeWidth={2} aria-hidden />
        </button>
      </div>
      {/* A grid, not a flex column (LED-666): a capped flex column SHRINKS
          its children instead of scrolling. Grid rows keep their height. */}
      <div data-testid="daily-log-editor-content" className="noscroll" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", alignContent: "start", minHeight: 0, overflowY: "auto", padding: "16px 18px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <KindPill label={kindLabel} />
          <span style={{ ...MONO, fontWeight: 600, fontSize: 10.5, letterSpacing: "1.2px", color: DL.muted, whiteSpace: "nowrap" }}>{whenStamp(item.createdAt).replace(/, \d{4}/, "")}</span>
        </div>
        <TitleEditor
          value={title ?? item.title}
          disabled={pending}
          onChange={(next) => { setTitle(next); if (next.trim()) save.change({ title: next.trim() }); }}
          onBlur={() => void save.flush()}
        />

        {pending && <div data-testid="daily-log-editor-pending" style={{ marginTop: 14, padding: "11px 12px", borderRadius: 12, background: DL.conflictBg, fontSize: 13.5, lineHeight: 1.45, color: DL.ink2 }}>
          This entry is still waiting in review.
          <a href={reviewHref} data-testid="daily-log-editor-review" onClick={(event) => { event.preventDefault(); onNavigate(reviewHref); }} style={{ display: "block", marginTop: 8, padding: "9px 12px", borderRadius: 10, background: DL.ink, color: DL.card, fontWeight: 700, textAlign: "center" }}>Review it →</a>
        </div>}

        {conflict ? (
          <div style={{ marginTop: 14 }} data-testid="daily-log-editor-conflict">
            <ManpowerConflictPanel
              company={conflict.walk.company || conflict.existing.company}
              where={`the ${shortDay(`${merge!.logDate}T12:00:00`) || logDay} Procore log`}
              existing={conflict.existing}
              walk={conflict.walk}
              variant={conflict.variant}
              choice={conflict.choice}
              onChoice={setMergeChoice}
              disabled={conflictBusy}
              testId="daily-log-conflict"
            />
            <button type="button" className="kb" data-testid="daily-log-conflict-save" disabled={conflictBusy} onClick={() => void applyConflict()} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, width: "100%", height: 40, marginTop: 10, borderRadius: 20, border: 0, background: DL.ink, color: DL.card, fontFamily: FONT_TEXT, fontSize: 13.5, fontWeight: 700, cursor: conflictBusy ? "default" : "pointer", opacity: conflictBusy ? 0.6 : 1 }}>
              <Check size={12} strokeWidth={2.6} aria-hidden />
              {conflictBusy ? "Saving…" : manpowerConflictActionLabel(conflict.choice, "Save")}
            </button>
            {conflictError && <div role="alert" style={{ marginTop: 8, fontSize: 12.5, color: DL.failInk }}>{conflictError}</div>}
            <div style={{ marginTop: 12 }} onBlur={() => void save.flush()}>
              <FieldRows layout="pills" proseOnly item={editorItem} search={search} onPatchChange={save.change} onCommit={() => void save.flush()} />
            </div>
          </div>
        ) : (
          <div data-testid="daily-log-editor-fields" style={{ marginTop: 14 }}>
            {pending ? fields.filter((field) => field.value !== "Not set").map((field) => (
              <div key={field.key} style={{ padding: "9px 0", borderBottom: `1px solid ${DL.rowLine}` }}>
                <span style={{ ...MONO, fontSize: 9.5, letterSpacing: "1.1px", color: DL.label }}>{field.label}</span>
                <div style={{ fontSize: 14.5, lineHeight: 1.45 }}>{field.value}</div>
              </div>
            )) : (
              <div onBlur={() => void save.flush()}>
                <FieldRows layout="pills" item={editorItem} search={search} onPatchChange={save.change} onCommit={() => void save.flush()} />
              </div>
            )}
          </div>
        )}

        <EntryMedia
          photos={photos}
          row={item}
          busy={mediaBusy}
          error={mediaError}
          canEdit={!pending}
          onAdd={() => setPickerOpen(true)}
          onRemove={(id) => void changeMedia(async () => [await deleteMutation(`${mediaPath}/${encodeURIComponent(id)}`, search)])}
        />
        {failure && <div role="status" style={{ marginTop: 8, fontSize: 13, color: DL.sub }}>Could not load this entry’s details. <button type="button" className="kb" onClick={() => setRevision((value) => value + 1)} style={{ color: C.goldInk }}>Retry</button></div>}

        <div style={{ marginTop: 16, borderTop: `1px solid ${DL.hairline}` }}>
          <MetaRow testId="daily-log-editor-creator" icon={<UserRound size={13} strokeWidth={2} aria-hidden />} label="Creator" value={creator ?? "—"} />
          <MetaRow icon={<MapPin size={13} strokeWidth={2} aria-hidden />} label="Walk" value={walk?.name ?? "Not from a walk"} />
          <MetaRow testId="daily-log-editor-sync" icon={<Check size={13} strokeWidth={2} aria-hidden />} label="Procore" value={procoreLine} />
        </div>

        <div style={{ display: "flex", alignItems: "center", marginTop: 18 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700 }}>Activity</span>
          <span style={{ flex: 1 }} />
          <span style={{ ...MONO, fontWeight: 400, fontSize: 9, letterSpacing: "1px", color: DL.label }}>Newest first</span>
        </div>
        <div data-testid="daily-log-editor-activity" style={{ marginTop: 6 }}>
          {activity.map((line) => (
            <div key={line.id} data-testid="daily-log-activity" data-id={line.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 0", borderBottom: `1px solid ${DL.rowLine}` }}>
              <span aria-hidden style={{ flex: "none", width: 22, height: 22, borderRadius: 11, background: MARK_TONE[line.tone].bg, color: MARK_TONE[line.tone].fg, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800 }}>{line.mark}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, lineHeight: 1.4, color: DL.ink2 }}>
                  {line.who && <strong style={{ fontWeight: 700, color: DL.ink }}>{line.who}</strong>}{line.who ? " " : ""}{line.what}
                </div>
                {line.when && <div style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: "0.8px", color: DL.label, marginTop: 2 }}>{line.when}</div>}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 14 }}>
          <span role={save.state.kind === "failed" ? "alert" : "status"} data-testid="daily-log-editor-save-state" data-state={save.state.kind} data-sync={syncPhase} style={{ ...MONO, fontWeight: 600, fontSize: 9.5, letterSpacing: "1px", color: saveLine.fg, lineHeight: 1.4, minWidth: 0 }}>
            {saveLine.text}
            {save.state.kind === "failed" && <button type="button" className="kb" onClick={() => void save.flush()} style={{ marginLeft: 8, color: C.goldInk, fontFamily: FONT_TEXT, fontSize: 12.5, fontWeight: 700, textTransform: "none", letterSpacing: 0 }}>Retry</button>}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 10, flex: "none" }}>
            <a href={detailHref} data-testid="daily-log-editor-open" onClick={(event) => { event.preventDefault(); void leave(onOpenDetail); }} style={{ color: DL.sub, fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>Full record ↗</a>
            {!pending && (
              <button
                type="button"
                className="kb dl-delete"
                data-testid="daily-log-editor-delete"
                data-armed={deleteArmed ? "true" : "false"}
                title={deleteArmed ? "Click again to delete" : "Delete entry"}
                aria-label={deleteArmed ? "Confirm delete" : "Delete entry"}
                disabled={deleteBusy}
                onClick={() => void remove()}
                onBlur={() => setDeleteArmed(false)}
                style={{
                  height: 34, minWidth: 34, padding: deleteArmed ? "0 12px" : 0, borderRadius: 17,
                  border: `1px solid ${deleteArmed ? DL.deleteLine : DL.control}`, background: deleteArmed ? DL.deleteHover : DL.card,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer",
                  color: deleteArmed ? DL.failInk : DL.ink, fontFamily: FONT_TEXT, fontSize: 12.5, fontWeight: 700,
                }}
              >
                <X size={12} strokeWidth={2.4} aria-hidden />
                {deleteArmed && (deleteBusy ? "Deleting…" : "Delete")}
              </button>
            )}
          </span>
        </div>
        {deleteError && <div role="alert" style={{ marginTop: 8, fontSize: 12.5, color: DL.failInk }}>{deleteError}</div>}
      </div>
      {pickerOpen && <RecordPhotoPicker
        visitId={row.visitId}
        walkName={walk?.name}
        recordTitle={item.title}
        attachedIds={photos.map((photo) => photo.id)}
        search={search}
        busy={mediaBusy}
        error={mediaError}
        onAttach={async (ids) => {
          await changeMedia(async () => { const out = []; for (const mediaId of ids) out.push(await postMutation(mediaPath, search, { mediaId })); return out; });
          setPickerOpen(false);
        }}
        onUpload={async (files) => {
          await changeMedia(async () => { const out = []; for (const file of files) out.push(await postMutation(mediaPath, search, await readRecordPhoto(file))); return out; });
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
      />}
    </div>
  );
}

const iconButton: CSSProperties = { width: 28, height: 28, borderRadius: 7, border: 0, background: "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };

/** The pane's chrome, shared with the walk photos pane. */
export { MONO as PANE_MONO, iconButton as PANE_ICON_BUTTON };
